// ── usePolymarketRelayFills ─────────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/fills. Returns the
// trader's persisted poly fill history (server-side polled from the
// CLOB by polyFillsPoller and deduped via polymarket_fill_id).
//
// Mirrors usePolymarketRelayOrders' shape so consumers can render an
// "unavailable" state with actionable copy when the relay can't reach
// the org's poly credential, instead of just an empty table.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listPolyFillsViaRelay,
  type PolyRelayFill,
  type PolyRelayUnavailableCode,
} from '@/lib/polymarket/relay';

export type PolyRelayFillsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyRelayFillsState {
  fills: PolyRelayFill[];
  status: PolyRelayFillsStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  lastFetchMs: number | null;
}

export interface UsePolymarketRelayFillsOptions {
  /** Off → hook is inert (no fetch, no poll). Defaults to true. */
  enabled?: boolean;
  /** Poll interval in ms. Defaults to 10000 (fills are less time-sensitive
   *  than open orders, so we don't need the 5s cadence). */
  pollMs?: number;
  /** Cap on fills returned. Forwarded as a query param. */
  limit?: number;
}

export interface UsePolymarketRelayFillsReturn extends PolyRelayFillsState {
  refresh: () => Promise<void>;
}

const DEFAULT_POLL_MS = 10_000;

export function usePolymarketRelayFills(
  options: UsePolymarketRelayFillsOptions = {},
): UsePolymarketRelayFillsReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const limit = options.limit;

  const [fills, setFills] = useState<PolyRelayFill[]>([]);
  const [status, setStatus] = useState<PolyRelayFillsStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [unavailableCode, setUnavailableCode] = useState<PolyRelayUnavailableCode | null>(null);
  const [lastFetchMs, setLastFetchMs] = useState<number | null>(null);

  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus((prev) => (prev === 'idle' ? 'loading' : prev));
    try {
      const out = await listPolyFillsViaRelay(limit);
      setLastFetchMs(Date.now());
      if (out.ok) {
        setFills(out.fills);
        setError(null);
        setUnavailableCode(null);
        setStatus('ready');
      } else if (out.code) {
        setFills([]);
        setError(out.error);
        setUnavailableCode(out.code);
        setStatus('unavailable');
      } else {
        setError(out.error);
        setUnavailableCode(null);
        setStatus('error');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [limit]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    const tick = () => {
      setStatus((current) => {
        if (!stopped && current !== 'unavailable') {
          void refresh();
        }
        return current;
      });
    };
    const intervalId = window.setInterval(tick, pollMs);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, pollMs, refresh]);

  return { fills, status, error, unavailableCode, lastFetchMs, refresh };
}
